import React, { Suspense } from 'react'

import classnames from 'classnames'
import AnimatedNumber from 'react-animated-number/build/AnimatedNumber'
import Picture from 'react-picture-component'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { ReactComponent as DeliveryMinTotalIcon } from '@kolayyolla/common/images/deliveryMinTotal.svg'
import { ReactComponent as DistanceIcon } from '@kolayyolla/common/images/distance.svg'
import { ReactComponent as ETAIcon } from '@kolayyolla/common/images/eta.svg'
import { ReactComponent as WalletIcon } from '@kolayyolla/common/images/wallet.svg'
import { animatedNumberConfig } from '@kolayyolla/common/utils/globalVars'

import { AddCircle } from '@mui/icons-material'
import { Card, CardActionArea, CardContent,  Divider, Skeleton, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'

const useStyles = makeStyles((theme) => {
  const classList = {

    product: {
      display: 'grid',
      gridTemplateColumns: '50px 1fr 70px',
      gridTemplateRows: '55px',
      gap: '1px 1px',
      gridTemplateAreas: '"image name price"',
      alignItems: 'center',
      padding: '2px 0px',
      transition: 'all 500ms ease 0s',
      cursor: 'pointer',
      '& img': {
        maxWidth: '100%',
        margin: 'auto',
        borderRadius: theme.spacing(1)
      },
      '& svg': {
        height: '1.75rem',
        width: '1.75rem'
      },
      '& .image': {
        gridArea: 'image',
        alignSelf: 'center',
        justifySelf: 'center',
        textAlign: 'center'
      },
      '& .name': {
        gridArea: 'name',
        fontSize: theme.typography.fontSize,
        '& > span': {
          display: 'flex',
          alignItems: 'flex-end',
          fontSize: theme.typography.fontSize - 2,
          fontStyle: 'italic',
          '& svg': {
            marginLeft: 5,
            height: 18,
            width: 18
          }

        }
      },
      '& .description': {
        gridArea: 'description',
        display: 'none'
      },
      '& .price': {
        gridArea: 'price',
        color: theme.palette.primary.main,
        whiteSpace: 'nowrap',
        textAlign: 'right',

        fontSize: theme.typography.fontSize,
        '& .oldPrice': {
          textDecoration: 'line-through',
          color: theme.palette.primary.light
        }
      },
      '& .clickarea': {
        position: 'absolute',
        width: '100%',
        height: '100%',
        zIndex: 10
      }
    },
    productsList: {
      margin: theme.spacing(1)
    },

    card: {
      margin: theme.spacing(2, 1),
      backgroundColor: theme.palette.background.paper,
      [theme.breakpoints.down('md')]: {
        maxWidth: '100%',
        width: '100%'
      },
      maxWidth: `calc(50% - ${theme.spacing(2)})`

    },

    cardHeader: {
      display: 'grid',
      transform: 'translateY(-55px)',
      marginBottom: -50,
      alignItems: 'center',
      gridTemplateColumns: '90px 1fr',
      [theme.breakpoints.only('xs')]: {
        gridTemplateColumns: '70px 1fr'
      }
    },

    cardMedia: {
      height: 120,
      backgroundPosition: 'center',
      backgroundSize: 'cover'
    },
    cardLogo: {
      zIndex: 2,
      width: 80,
      height: 80,
      bottom: 0,
      left: 32,
      borderRadius: '50%',
      overflow: 'hidden',
      backgroundColor: 'white',
      border: '2px solid #fff',
      boxShadow: 'rgba(0,0,0,0.2) 0px 2px 8px',
      display: 'flex',
      alignItems: 'center',
      [theme.breakpoints.only('xs')]: {
        width: 60,
        height: 60
      },
      '& img': {
        [theme.breakpoints.only('xs')]: {
          maxWidth: 55,
          maxHeight: 55
        },
        maxWidth: 75,
        maxHeight: 75
      }

    },
    footer: {
      color: 'rgb(118, 118, 118)',
      fontSize: 14,
      letterSpacing: '0ch',
      textTransform: 'none',
      padding: 0,
      display: 'flex',
      flexDirection: 'row',
      gap: 4,
      justifyContent: 'space-evenly',

      '& > div': {
        flexBasis: '100%',
        padding: theme.spacing(1, 0)
      },

      '& .start': {
        textAlign: 'start'
      },
      '& .center': {
        textAlign: 'center'
      },
      '& .end': {
        textAlign: 'end'
      }
    },
    noHeaderLogo: {
      marginBottom: '0px!important',
      transform: 'inherit!important'
    },
    offline: {
      '& >* ': {
        opacity: 0.5
      }
    },
    fullWidth: {
      maxWidth: '100%!important',
      width: '100%!important'
    }
    // cardHovered: {
    //   transform: 'scale3d(1.05, 1.05, 1)'
    // }

  }

  return classList
})

export default function VenueCard ({ venue, style, onMouseOver, onMouseLeave, fullWidth }) {
  const classes  = useStyles()
  const navigate = useNavigate()
  // const [hovered, setHovered] = React.useState(false)

  const categories = useSelector((state) => state.category.data)

  const renderProducts = (v) => {
    return v.products.map((p) => (
      <Suspense key={p._id} fallback={<Skeleton animation="wave" />}>

        <div
          className={classes.product}
          onKeyPress={() => { navigate(`/${v.alias}?p=${p._id}`) }}
          onClick={() => { navigate(`/${v.alias}?p=${p._id}`) }}
          role="button"
          tabIndex={0}
        >
          <div className="image">
            {p?.image?.sm
              ? <img src={p.image.sm} alt="img" />
              : <AddCircle color="secondary" />}
          </div>
          <div className="name">
            {p.name}
            {
              ((p.extraPoints) &&
                v.loyalty.settings.inShopActive)
                ? (
                  <span>
                    <WalletIcon />
                    {`% ${p?.extraPoints} ekstra KumPara!`}
                  </span>
                  ) : null
            }
          </div>
          <div className="description">
            {p.description}
          </div>
          <div className="price">
            {(p.oldPrice)
              ? (
                <>
                  <AnimatedNumber value={p.price} {...animatedNumberConfig('1.1rem')} />
                  <br />
                  <span className="oldPrice">
                    <AnimatedNumber value={p.oldPrice} {...animatedNumberConfig('1.1rem')} />
                  </span>
                </>
                )
              : (
                <p>
                  <AnimatedNumber value={p.price} {...animatedNumberConfig('1.1rem')} />
                </p>
                )}
          </div>
        </div>
      </Suspense>
    ))
  }

  const addressInfoContent = (v) => {
    if (v.canOrder) {
      return (
        <>
          <div className="start">
            <DistanceIcon style={{ height: 16, width: 16, fill: 'rgb(118, 118, 118)' }} />
            {' '}
            {v.distance.toFixed(2)}
            km
          </div>
          <div className="center">
            <ETAIcon style={{ height: 16, width: 16, fill: 'rgb(118, 118, 118)' }} />
            {' '}
            {v.eta}
            {' '}
            dk
          </div>
          <div className="end">
            <DeliveryMinTotalIcon style={{ height: 16, width: 16, fill: 'rgb(118, 118, 118)' }} />
            {' '}
            {v.minTotal}
            ₺
          </div>
        </>
      )
    }
    return (
      <>
        <div>
          <DistanceIcon style={{ height: 16, width: 16, fill: 'rgb(118, 118, 118)' }} />
          {' '}
          {v.distance.toFixed(2)}
          km
        </div>
        <div>
          <DeliveryMinTotalIcon style={{ height: 16, width: 16, fill: 'rgb(118, 118, 118)' }} />
          {' '}
          {v.minTotal}
          ₺
        </div>
        <div>
          Şu an sipariş alamıyor
        </div>
      </>
    )
  }
  return (
    <Card className={classnames(
      classes.card,
      (venue.canOrder) ? null : classes.offline,
      fullWidth && classes.fullWidth
      // hovered && classes.cardHovered
    )}
      onMouseOver={(e) => {
        // if (hoverable) {
        //   setHovered(true)
        // }
        onMouseOver && onMouseOver(e)
      }}
      onMouseLeave={(e) => {
        onMouseLeave && onMouseLeave(e)
      }}
      elevation={1}
      style={style}>
      <CardActionArea onClick={() => navigate(`/${venue.alias}`)}>

        {
          venue.heroImage?.lg && <div
            className={classes.cardMedia}
            style={{ backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0) 35%, rgba(255,255,255,1) 85%), url(${venue.heroImage.lg})` }}
          />
        }
        <CardContent style={{ paddingBottom: 0 }}>

          <div className={classnames(classes.cardHeader, venue.heroImage?.lg ? null : classes.noHeaderLogo)}>
          {
              venue.logo?.md
                ? (
                  <div className={classes.cardLogo} >
                    <Picture images={{ 300: venue.logo?.md }} />
                  </div>
                  )
                : null
            }
          <Typography gutterBottom variant="h5" component="h5" >
            {venue.name}
          </Typography>
          </div>

          {
            venue?.loyalty?.settings?.isActive && (
              <Typography variant="subtitle2" gutterBottom>
                {'İlk paket siparişinize % '}
                {venue.loyalty.settings.firstVisitRate}
                {' '}
                <WalletIcon style={{ height: 16, width: 16 }} />
                {' KumPara!'}
              </Typography>
            )
          }
          {
            venue?.loyalty?.settings?.isActive && (
              <Typography variant="subtitle2" gutterBottom>
                Sonraki biriktiren siparişlerinizde min %
                {venue.loyalty.settings.minimumRate}
                {' '}
                <WalletIcon style={{ height: 16, width: 16 }} />
                {' KumPara!'}
              </Typography>
            )
          }

          <Typography gutterBottom variant="caption">
            {venue.categories.map((c) => categories.find((cat) => cat.key === c)?.name).join(', ')}
          </Typography>

          <div className={classes.footer}>{addressInfoContent(venue)}</div>

        </CardContent>

      </CardActionArea>
      {
        venue.products && (
          <div className={classes.productsList}>
            <Divider />
            {renderProducts(venue)}
          </div>
        )
      }
    </Card>
  )
}
